import { useState, useEffect } from 'react';

/* The script for the slide on screen — nothing else.
   Shown only in the Practice Room (Presentations.jsx with withScript).

   Props
     data          { steps, bySlide } from buildDeckScripts
     slide         the slide number the deck is showing
     onOpenChange  (open) => void — lets the page make room beside the slides */

const CSS = `
.sd-tab{position:fixed;right:0;top:50%;transform:translateY(-50%);z-index:10002;
  background:linear-gradient(135deg,#E0B24A,#C9922A);color:#20321A;font:800 12px Montserrat,system-ui,sans-serif;
  letter-spacing:.1em;padding:16px 9px;border:none;border-radius:12px 0 0 12px;cursor:pointer;
  writing-mode:vertical-rl;box-shadow:-4px 0 18px -6px rgba(0,0,0,.55);transition:padding .2s}
.sd-tab:hover{padding-right:13px}
.sd-tab:focus-visible{outline:3px solid #fff;outline-offset:2px}
.sd{position:fixed;right:0;top:0;bottom:0;width:min(440px,46vw);z-index:10002;display:flex;flex-direction:column;
  background:#fcfdfc;border-left:3px solid #C9922A;box-shadow:-12px 0 40px -14px rgba(0,0,0,.65);
  font-family:Montserrat,system-ui,sans-serif;color:#0F241D;animation:sdIn .26s cubic-bezier(.2,.8,.3,1)}
@keyframes sdIn{from{transform:translateX(100%)}to{transform:none}}
@media(max-width:760px){.sd{width:100vw}}
.sd-head{padding:13px 16px 12px;background:#FBF8F1;border-bottom:1px solid #EAEFEC}
.sd-top{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
.sd-step{font-size:12.5px;font-weight:800;color:#8A6B1F;line-height:1.3}
.sd-pos{font-size:10.5px;font-weight:700;color:#9AA69F;margin-top:3px;letter-spacing:.04em}
.sd-x{width:28px;height:28px;flex:0 0 auto;border-radius:50%;border:none;background:#EEF3F0;color:#4A5D55;
  font-size:13px;cursor:pointer}
.sd-x:hover{background:#E1E9E4}
.sd-body{flex:1;overflow:auto;padding:13px 16px;background:#fff}
.sd-say{background:linear-gradient(135deg,#eaf6ef,#f3fbf5);border-left:4px solid #1B5E20;border-radius:9px;
  padding:10px 12px;margin-bottom:8px}
.sd-say b{display:block;font-size:9.5px;font-weight:800;letter-spacing:.09em;color:#1B5E20;margin-bottom:4px}
.sd-say i{font-size:13.5px;line-height:1.5;color:#143a26;font-style:italic}
.sd-do{background:#FFF8E6;border-left:4px solid #C9922A;border-radius:9px;padding:8px 12px;margin-bottom:8px;
  font-size:12.5px;color:#6B5200;font-weight:600}
.sd-do b{display:block;font-size:9.5px;font-weight:800;letter-spacing:.09em;color:#8A6B1F;margin-bottom:3px}
.sd-video{margin-top:14px;padding-top:12px;border-top:1px dashed #DCE5E0}
.sd-video b{display:block;font-size:9.5px;font-weight:800;letter-spacing:.09em;color:#0D3B2E;margin-bottom:6px}
.sd-video video{width:100%;display:block;border-radius:10px;background:#000;aspect-ratio:16/9}
.sd-video video + video{margin-top:10px}
.sd-empty{font-size:13px;color:#7c8a83;padding:14px 2px;line-height:1.5}
.sd-foot{border-top:1px solid #EAEFEC;padding:10px 16px;background:#F8FAF9}
.sd-kbdline{font-size:10.5px;font-weight:800;letter-spacing:.08em;color:#9AA69F}
.sd-kbd{display:inline-block;background:#fff;border:1px solid #D5DEDA;border-bottom-width:2px;border-radius:5px;
  padding:0 5px;font-size:10px;font-weight:800;color:#4A5D55}
`;

export default function ScriptDrawer({ data, slide, onOpenChange }) {
  const [open, setOpen] = useState(false);

  // tell the page, so it can make room for the drawer beside the slides
  useEffect(() => { if (onOpenChange) onOpenChange(open); }, [open, onOpenChange]);

  const here = data?.bySlide?.[slide] || null;

  // S opens and closes the drawer
  useEffect(() => {
    function key(e) {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 's' || e.key === 'S') { if (here || open) setOpen(o => !o); }
    }
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [here, open]);

  // no script for this slide: no tab at all, so nothing sits on an empty slide
  if (!open && !here) return <style>{CSS}</style>;

  return (
    <>
      <style>{CSS}</style>

      {!open && here && (
        <button className="sd-tab" onClick={() => setOpen(true)} title="Show the script (S)">SCRIPT</button>
      )}

      {open && (
        <aside className="sd" aria-label="Script">
          <div className="sd-head">
            <div className="sd-top">
              <div>
                <div className="sd-step">{here ? here.label : 'No script on this slide'}</div>
                <div className="sd-pos">{here ? `SLIDE ${slide} · ${(here.stepTitle || "").toUpperCase()}` : `SLIDE ${slide}`}</div>
              </div>
              <button className="sd-x" onClick={() => setOpen(false)} aria-label="Close script">✕</button>
            </div>
          </div>

          <div className="sd-body">
            {here
              ? here.lines.map((l, i) => {
                  const text = typeof l === 'string' ? l : l.t;
                  return (l && l.kind === 'do')
                    ? <div key={i} className="sd-do"><b>DO THIS</b>{text.replace(/^\(|\)\.?$/g, '')}</div>
                    : <div key={i} className="sd-say"><b>SAY THIS</b><i>{text}</i></div>;
                })
              : <div className="sd-empty">There is no script written for this slide.</div>}
            {here && here.video && (
              <div className="sd-video">
                {/* one video or several (slide 5 has two); key = file, so a new slide loads its own and stops the last */}
                {[].concat(here.video).map(v => (
                  <video key={v} src={v} controls playsInline preload="metadata" />
                ))}
              </div>
            )}
          </div>

          <div className="sd-foot">
            <div className="sd-kbdline">PRESS <span className="sd-kbd">S</span> TO OPEN AND CLOSE</div>
          </div>
        </aside>
      )}
    </>
  );
}
